import { Button, Link, MenuItem } from "@chakra-ui/react";
import EditRoleModal from "features/settings/users/EditRoleModal";
import InviteUserModal from "features/settings/users/InviteUserModal";
import { UserRoleDefinitionsModal } from "features/settings/users/modal/UserRoleDefinitionsModal";
import RemoveUserModal from "features/settings/users/RemoveUserModel";
import {
  getUserRetrieveQueryKey,
  getUsersAdminTableDataRetrieveQueryKey,
  useUserRoleDescriptionsListSuspense,
  useUsersAdminTableDataRetrieveSuspense,
} from "gen/endpoints/users/users";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RenderRowActionsFn } from "shared/components/table/data/DataTableTypes";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const UsersPage: React.FC = () => {
  const { t } = useTranslation(["common", "settings", "internal"]);

  const { data: tableData } = useUsersAdminTableDataRetrieveSuspense({
    edits_enabled: false,
  });
  const { data: roleDescriptions } = useUserRoleDescriptionsListSuspense();

  const {
    value: isInviteUserModalOpen,
    setTrue: openInviteUserModal,
    setFalse: closeInviteUserModal,
  } = useBoolean();

  const {
    value: isRoleDefinitionsModalOpen,
    setTrue: openRoleDefinitionsModal,
    setFalse: closeRoleDefinitionsModal,
  } = useBoolean();

  const {
    value: isEditRoleModalOpen,
    setTrue: openEditRoleModal,
    setFalse: closeEditRoleModal,
  } = useBoolean();

  const {
    value: isRemoveUserModalOpen,
    setTrue: openRemoveUserModal,
    setFalse: closeRemoveUserModal,
  } = useBoolean();

  const [currentUserId, setCurrentUserId] = useState("");

  const invalidateQueries = useInvalidateQueryKeys();
  const onSuccess = async () => {
    await invalidateQueries(
      getUsersAdminTableDataRetrieveQueryKey({ edits_enabled: true }),
      getUsersAdminTableDataRetrieveQueryKey({ edits_enabled: false }),
      getUserRetrieveQueryKey(currentUserId),
    );
  };

  const userTableRenderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type === "id_row") {
        const userId = metadata.id;
        return {
          type: "menu",
          actions: (
            <>
              <MenuItem
                onClick={() => {
                  setCurrentUserId(userId);
                  openEditRoleModal();
                }}
              >
                {t("settings:user.editRole.openModal")}
              </MenuItem>
              <MenuItem
                className="destructive"
                onClick={() => {
                  setCurrentUserId(userId);
                  openRemoveUserModal();
                }}
              >
                {t("settings:user.remove.openModal")}
              </MenuItem>
            </>
          ),
        };
      }
      return undefined;
    },
    [openEditRoleModal, openRemoveUserModal, t],
  );

  return (
    <PageTabBody>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("common:users")}</TableBoxTitle>
          <DataTableBoxActions>
            <Link mr={2} onClick={openRoleDefinitionsModal}>
              {t("settings:roleDescriptions")}
            </Link>
            <Button size="xs" onClick={openInviteUserModal}>
              {t("settings:user.invite.button")}
            </Button>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            renderRowActions={userTableRenderRowActions}
          />
        </TableBoxBody>
      </TableBox>
      <QueryBoundary>
        {isInviteUserModalOpen && (
          <InviteUserModal
            roleDefinitions={roleDescriptions}
            onSuccess={async () => {
              await onSuccess();
              closeInviteUserModal();
            }}
            onCloseComplete={closeInviteUserModal}
          />
        )}
        {isRoleDefinitionsModalOpen && (
          <UserRoleDefinitionsModal
            onCloseComplete={closeRoleDefinitionsModal}
          />
        )}
        {isEditRoleModalOpen && (
          <EditRoleModal
            userId={currentUserId}
            roleDefinitions={roleDescriptions}
            onCloseComplete={closeEditRoleModal}
            onSuccess={async () => {
              await onSuccess();
              closeEditRoleModal();
            }}
          />
        )}
        {isRemoveUserModalOpen && (
          <RemoveUserModal
            userId={currentUserId}
            onCloseComplete={closeRemoveUserModal}
            onSuccess={async () => {
              await onSuccess();
              closeRemoveUserModal();
            }}
          />
        )}
      </QueryBoundary>
    </PageTabBody>
  );
};
